<template>
  <div>
    <div
      v-if="shouldShow"
      class="session-timeout-renew-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-timeout-renew-title"
    >
      <div class="session-timeout-renew-card">
        <h5 id="session-timeout-renew-title" class="session-timeout-renew-title">
          {{ $t('session_timeout.warning_title') }}
        </h5>

        <div class="session-timeout-renew">
          <div class="renew-icon">
            <feather-icon icon="AlertCircleIcon" size="36" />
          </div>
          <p class="renew-message">
            {{ $t('session_timeout.warning_message') }}
          </p>
          <p class="renew-countdown">
            {{ $t('session_timeout.remaining', { time: formattedTime }) }}
          </p>
        </div>

        <div class="session-timeout-renew-footer">
          <button
            type="button"
            class="btn btn-primary"
            @click.stop.prevent="continueSession"
          >
            {{ $t('session_timeout.renew') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FeatherIcon from '@/components/FeatherIcon.vue'
import sessionTimeoutService from '@/services/SessionTimeoutService'
import { isUserLoggedIn } from '@/util/Util'

export default {
  name: 'SessionTimeoutRenewPopup',
  components: {
    FeatherIcon
  },
  data() {
    return {
      remainingMs: 0,
      expired: false,
      unsubscribe: null,
      unsubscribeExpired: null
    }
  },
  computed: {
    isLoggedIn() {
      return isUserLoggedIn() && Boolean(this.$store.getters.userToken)
    },
    warningMs() {
      return sessionTimeoutService.getConfig().warningMs
    },
    shouldShow() {
      if (!sessionTimeoutService.isEnabled() || !this.isLoggedIn || this.expired) return false
      if (this.$route.path === '/login' || this.$route.path === '/a2fa') return false
      return this.remainingMs > 0 && this.remainingMs <= this.warningMs
    },
    formattedTime() {
      const totalSeconds = Math.max(0, Math.ceil(this.remainingMs / 1000))
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60
      const pad = value => String(value).padStart(2, '0')

      if (hours > 0) {
        return `${hours}:${pad(minutes)}:${pad(seconds)}`
      }

      return `${pad(minutes)}:${pad(seconds)}`
    }
  },
  methods: {
    continueSession() {
      sessionTimeoutService.renew()
    }
  },
  mounted() {
    document.body.appendChild(this.$el)
    this.unsubscribe = sessionTimeoutService.subscribe(remainingMs => {
      this.remainingMs = remainingMs
    })
    this.unsubscribeExpired = sessionTimeoutService.subscribeExpirePrompt(active => {
      this.expired = active
    })
  },
  beforeDestroy() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }
    if (this.unsubscribe) {
      this.unsubscribe()
    }
    if (this.unsubscribeExpired) {
      this.unsubscribeExpired()
    }
  }
}
</script>

<style lang="scss">
.session-timeout-renew-overlay {
  position: fixed !important;
  inset: 0;
  z-index: 20000010 !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(34, 41, 47, 0.55);
}

.session-timeout-renew-card {
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 0.428rem;
  box-shadow: 0 8px 32px rgba(34, 41, 47, 0.28);
}

.session-timeout-renew-title {
  margin: 0;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #ebe9f1;
  font-size: 1.1rem;
  font-weight: 600;
  color: #5e5873;
}

.session-timeout-renew {
  padding: 1.25rem 1.25rem 0.5rem;
  text-align: center;

  .renew-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    border-radius: 50%;
    color: #c2410c;
    background-color: rgba(251, 146, 60, 0.16);
  }

  .renew-message {
    margin-bottom: 0.75rem;
    color: #5e5873;
    line-height: 1.5;
  }

  .renew-countdown {
    margin-bottom: 0;
    font-size: 1.05rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: #c2410c;
  }
}

.session-timeout-renew-footer {
  display: flex;
  justify-content: flex-end;
  padding: 1rem 1.25rem;
  border-top: 1px solid #dee2e6;

  .btn {
    min-width: 150px;
  }
}

.dark-layout .session-timeout-renew-card {
  background: #283046;
}

.dark-layout .session-timeout-renew-title,
.dark-layout .session-timeout-renew .renew-message {
  color: #d0d2d6;
}

.dark-layout .session-timeout-renew-title,
.dark-layout .session-timeout-renew-footer {
  border-color: #3b4253;
}
</style>
